import { Alert, Box, SelectChangeEvent, Typography } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { PartnerIntakeContext } from '@/Contexts/PartnerIntakeContext';
import usePartner from '@/hooks/usePartner';
import { PartnerProps } from '@/models/Partner';
import { ProgramNames } from '@/models/Program';
import { USCountryId } from '@/utils/constants';

import { SelectInput } from '../../../common/select_input/SelectInput';
import { Spinner } from '../../../common/Spinner';
import { logError } from '@/utils/errorHandlerUtil';

const RowWrap = ({ children }: { children: React.ReactElement | React.ReactElement[] }) => {
  return (
    <Box
      width='80%'
      display='flex'
      flexGrow={1}
      flexDirection='row'
      flexWrap='wrap'
      justifyContent='space-between'
      gap={'20px'}
    >
      {children}
    </Box>
  );
};

function ExistingPartnerSelect() {
  const {
    partnerInfo,
    showErrors,
    updatePartnerGroupInfo,
    updatePartnerInfo,
    updateCountryId,
    updateStateId,
  } = useContext(PartnerIntakeContext);

  const { countrySelectOptions, stateSelectOptions } = useContext(ConfigContext);
  const { translatedText } = useContext(LocaleContext);
  const { getPartnerSimple } = usePartner();
  const { selectedProgramInfo } = useContext(PartnerIntakeContext);

  const [selectedStateId, setSelectedStateId] = useState<string | null>(null);
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [partnersLoading, setPartnersLoading] = useState<boolean>(false);
  const [availablePartners, setAvailablePartners] = useState<PartnerProps[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);

  const showCountrySelect = useMemo(() => {
    return selectedProgramInfo.id == ProgramNames.TreeCitiesWorld;
  }, [selectedProgramInfo.id]);

  const showStateSelect = useMemo(() => {
    return (
      selectedProgramInfo.id != ProgramNames.TreeCitiesWorld ||
      (selectedProgramInfo.id == ProgramNames.TreeCitiesWorld && selectedCountryId == USCountryId)
    );
  }, [selectedProgramInfo.id, selectedCountryId]);

  const partnerSelectOptions = useMemo(() => {
    return availablePartners != null && availablePartners.length > 0
      ? availablePartners
          .filter(
            (p: PartnerProps) => p.id != null && p.partnerName != null && p.partnerName !== ''
          )
          ?.sort(function (a: PartnerProps, b: PartnerProps) {
            if ((a.partnerName ?? '') < (b.partnerName ?? '')) {
              return -1;
            } else if ((a.partnerName ?? '') > (b.partnerName ?? '')) {
              return 1;
            }

            return 0;
          })
          .map((p: PartnerProps) => {
            return {
              value: p.id!,
              label: p.partnerName!,
            };
          })
      : [];
  }, [availablePartners]);

  const showPartnerSelect = partnerSelectOptions.length > 0;
  const partnerTerm = selectedProgramInfo?.partnerTerm;
  const programName = selectedProgramInfo?.programName;

  const showNoPartnersFoundAlert = useMemo(() => {
    if (partnersLoading || availablePartners.length > 0) return false;
    if (showCountrySelect && selectedCountryId == null) return false;
    if (showStateSelect && selectedStateId == null) return false;

    return true;
  }, [
    availablePartners.length,
    partnersLoading,
    selectedCountryId,
    selectedStateId,
    showCountrySelect,
    showStateSelect,
  ]);

  const delayThenHideSpinner = useCallback(() => {
    setTimeout(() => {
      setPartnersLoading(false);
    }, 500);
  }, []);

  useEffect(() => {
    setSelectedPartnerId(partnerInfo.id ?? null);
  }, [partnerInfo.id]);

  useEffect(() => {
    if (partnerInfo?.id == null || (partnerInfo?.id != null && partnerInfo?.stateId != null)) {
      setSelectedStateId(partnerInfo?.stateId ?? null);
    }
  }, [partnerInfo?.id, partnerInfo?.stateId]);

  useEffect(() => {
    if (partnerInfo?.id == null || (partnerInfo?.id != null && partnerInfo?.countryId != null)) {
      setSelectedCountryId(partnerInfo?.countryId ?? null);
    }
  }, [partnerInfo?.countryId, partnerInfo?.id]);

  useEffect(() => {
    const loadPartners = async (programId: number, stateId: string) => {
      setPartnersLoading(true);
      const x = await getPartnerSimple(programId, stateId);
      if (x.length > 0) {
        setAvailablePartners(x);
      }

      delayThenHideSpinner();
    };

    if (showStateSelect && selectedStateId != null) {
      void loadPartners(selectedProgramInfo.id, selectedStateId);
    }
  }, [
    showStateSelect,
    selectedStateId,
    selectedProgramInfo.id,
    getPartnerSimple,
    delayThenHideSpinner,
  ]);

  useEffect(() => {
    const loadPartners = async (programId: number, stateId: string, countryId: number) => {
      setAvailablePartners([]);
      setPartnersLoading(true);
      const result = await getPartnerSimple(programId, null, null, countryId);
      if (result.length > 0) {
        setAvailablePartners(result);
      }

      delayThenHideSpinner();
    };
    if (showCountrySelect && selectedCountryId != null && selectedCountryId != USCountryId) {
      void loadPartners(selectedProgramInfo.id, selectedStateId ?? '', selectedCountryId ?? 0);
    }
  }, [
    selectedProgramInfo.id,
    selectedStateId,
    selectedCountryId,
    showCountrySelect,
    getPartnerSimple,
    delayThenHideSpinner,
  ]);

  const handleCountryChange = useCallback(
    (countryId: number) => {
      setSelectedCountryId(countryId);
      setSelectedStateId(null);
      setAvailablePartners([]);

      if (updateCountryId != null) updateCountryId(countryId);
    },
    [updateCountryId]
  );

  const handleStateChange = useCallback(
    (stateId: string) => {
      setSelectedStateId(stateId);
      setAvailablePartners([]);

      if (updateStateId != null) updateStateId(stateId);
    },
    [updateStateId]
  );

  const handlePartnerSelect = useCallback(
    (selectedPartner: PartnerProps) => {
      if (updatePartnerInfo != null) updatePartnerInfo({ id: selectedPartner.id });
      if (updatePartnerGroupInfo != null)
        updatePartnerGroupInfo({ id: selectedPartner.partnerGroupId });
    },
    [updatePartnerGroupInfo, updatePartnerInfo]
  );

  // Form Flows:
  // TCUSA:   Select State        -> Select City
  // TCOW:    Select Country (US) -> Select State   -> Select City
  //                     (Non-US) -> Select City
  // TLine:   Select State        -> Select Utiltiy
  // TCHE:    Select State        -> Select School
  // TCHC:    Select State        -> Select Campus
  // TCK12:   Select State        -> Select School

  return (
    <>
      <RowWrap>
        <Box>
          <Typography variant='h3'>
            {`${translatedText.signup.partnerSelect}${partnerTerm}`}
          </Typography>
          <Typography sx={{ marginTop: '12px' }}>
            {translatedText.signup.subHeaderExistingPartnerDetails(
              partnerTerm != null && partnerTerm.length > 0 ? partnerTerm : 'Partner',
              programName.length > 0 ? programName : 'program'
            )}
          </Typography>
        </Box>
      </RowWrap>
      <RowWrap>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='left'
          justifyContent='left'
          flexGrow={1}
          gap={2}
          textAlign='left'
          marginBottom={0}
        >
          {showCountrySelect && (
            <Box
              sx={{
                width: '100%',
              }}
            >
              <SelectInput
                id={'partner-country'}
                label={translatedText.signup.countrySelect}
                hasError={showErrors && selectedCountryId == null}
                value={
                  selectedCountryId != null && countrySelectOptions != null
                    ? selectedCountryId.toString()
                    : ''
                }
                onChange={(e: SelectChangeEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCountryChange(Number(e.target.value));
                }}
                selectOptions={countrySelectOptions}
              />
            </Box>
          )}

          {showStateSelect && (
            <Box
              sx={{
                width: '100%',
              }}
            >
              <SelectInput
                id='partner-state'
                label={'Select Your State'}
                value={selectedStateId != null && stateSelectOptions != null ? selectedStateId : ''}
                hasError={showErrors && selectedStateId == null}
                selectOptions={stateSelectOptions}
                onChange={(e: SelectChangeEvent) => {
                  handleStateChange(e.target.value);
                }}
              />
            </Box>
          )}

          {showPartnerSelect ? (
            <Box
              sx={{
                width: '100%',
              }}
            >
              <SelectInput
                label={`${translatedText.signup.partnerSelect}${partnerTerm}`}
                id={'partner-select'}
                value={selectedPartnerId != null ? selectedPartnerId.toString() : ''}
                onChange={(e: SelectChangeEvent) => {
                  e.preventDefault();
                  e.stopPropagation();

                  const updatedPartnerId = Number(e.target.value);

                  setSelectedPartnerId(updatedPartnerId);

                  const selectedPartner = availablePartners.find((x: PartnerProps) => {
                    return x.id === updatedPartnerId;
                  });

                  if (selectedPartner?.id != null) {
                    handlePartnerSelect(selectedPartner);
                  } else {
                    logError(`Error finding selected partner with ID: ${updatedPartnerId}`);
                  }
                }}
                selectOptions={partnerSelectOptions}
              />
            </Box>
          ) : partnersLoading ? (
            <Spinner type='circular' />
          ) : showNoPartnersFoundAlert ? (
            <>
              <Alert severity='warning'>
                {translatedText.signup.partnerNotFoundHelperText(
                  partnerTerm != null && partnerTerm.length > 0
                    ? partnerTerm.toLowerCase()
                    : 'partner'
                )}
              </Alert>
            </>
          ) : (
            <></>
          )}
        </Box>
      </RowWrap>
    </>
  );
}

export default ExistingPartnerSelect;
